/**
 * IT-OFFBOARD-EVIDENCE-001 — HR read-only view of an IT offboarding case:
 * unreturned-asset findings, recovery values, IT notes, evidence downloads,
 * escalation records, and Resolve action for open recovery items.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Download, ShieldAlert } from 'lucide-react';
import { formatDateTimeET, formatDateOnly } from '../../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL || '';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_LABELS = {
  pending: 'Pending', in_progress: 'In Progress', awaiting_asset_return: 'Awaiting Asset Return',
  escalated_to_hr: 'Escalated to HR', cleared: 'Cleared', cancelled: 'Cancelled',
};
const fmtDT = (d) => d ? formatDateTimeET(d) + ' ET' : '—';
const money = (v) => (v === null || v === undefined) ? '—' : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

export default function HRClearanceModal({ caseId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [resolveNotes, setResolveNotes] = useState('');
  const [resolvingId, setResolvingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/it-offboarding/hr/cases/${caseId}`, { headers: headers() });
      setDetail(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load clearance record'); }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  const download = async (fileId, filename) => {
    try {
      const res = await axios.get(
        `${API}/api/it-offboarding/cases/${caseId}/attachments/${fileId}/download`,
        { headers: headers(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const resolveItem = async (itemId) => {
    if (!resolveNotes.trim()) { toast.error('Resolution notes are required'); return; }
    try {
      await axios.post(`${API}/api/it-offboarding/hr/recovery-items/${itemId}/resolve`,
        { notes: resolveNotes }, { headers: headers() });
      toast.success('Recovery item resolved');
      setResolvingId(null); setResolveNotes(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to resolve'); }
  };

  if (!caseId) return null;
  const c = detail?.case;
  const openItems = (detail?.recovery_items || []).filter(i => i.status === 'open');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="hr-clearance-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <ShieldAlert size={16} className="text-amber-600" />
            IT Clearance Record — {c?.employee_name || '…'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="hr-clearance-close"><X size={18} /></button>
        </div>
        {!detail ? <div className="p-6 text-sm text-slate-500">Loading…</div> : (
          <div className="p-5 overflow-y-auto text-sm space-y-4">
            <div className="flex flex-wrap gap-4 text-xs text-slate-600">
              <span>IT Status: <strong data-testid="hr-clearance-status">{STATUS_LABELS[c.clearance_status] || c.clearance_status}</strong></span>
              <span>LWD: <strong>{c.last_working_day ? formatDateOnly(c.last_working_day) : 'not set'}</strong></span>
              {c.cleared_at && <span>IT Cleared: <strong>{fmtDT(c.cleared_at)} by {c.cleared_by_name}</strong></span>}
              {openItems.length > 0 && <span className="text-red-600 font-semibold" data-testid="hr-recovery-open-flag">⚠ {openItems.length} recovery item(s) awaiting HR resolution</span>}
            </div>

            {(detail.recovery_items || []).map(item => (
              <div key={item.id} className={`border rounded-lg p-3 ${item.status === 'open' ? 'border-red-200 bg-red-50' : 'border-emerald-200 bg-emerald-50'}`} data-testid={`hr-recovery-item-${item.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-xs uppercase">{item.status === 'open' ? 'Open Recovery Item' : 'Resolved Recovery Item'}</span>
                  <span className="text-xs">Submitted by {item.submitted_by_name} · {fmtDT(item.submitted_at)}</span>
                </div>
                <table className="w-full text-xs mt-2">
                  <thead><tr className="text-left text-slate-500"><th className="py-1">Asset</th><th>Tag / Serial</th><th>Status</th><th>Purchase</th><th>Recovery</th></tr></thead>
                  <tbody>
                    {item.assets.map(f => (
                      <tr key={f.asset_id} className="border-t border-black/5">
                        <td className="py-1">{f.asset_name}</td>
                        <td>{f.asset_tag}{f.serial_number ? ` / ${f.serial_number}` : ''}</td>
                        <td className="capitalize">{(f.status || '').replace('_', ' ')}</td>
                        <td>{money(f.purchase_cost)}</td>
                        <td className="font-semibold">{money(f.recovery_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-1 text-xs font-semibold">Total recovery value: {money(item.total_recovery_value)}</div>
                {item.status === 'resolved' && (
                  <div className="mt-1 text-xs text-emerald-700">Resolved by {item.resolved_by_name} · {fmtDT(item.resolved_at)} — {item.resolution_notes}</div>
                )}
                {item.status === 'open' && (
                  resolvingId === item.id ? (
                    <div className="mt-2 flex gap-2">
                      <input className="flex-1 border rounded px-2 py-1 text-xs" value={resolveNotes}
                        onChange={e => setResolveNotes(e.target.value)} placeholder="Resolution notes (required)…"
                        data-testid="hr-resolve-notes-input" />
                      <button className="px-3 py-1 text-xs rounded bg-emerald-600 text-white" onClick={() => resolveItem(item.id)} data-testid="hr-resolve-confirm">Confirm</button>
                      <button className="px-3 py-1 text-xs rounded border" onClick={() => setResolvingId(null)}>Cancel</button>
                    </div>
                  ) : (
                    <button className="mt-2 px-3 py-1 text-xs rounded bg-emerald-600 text-white" onClick={() => setResolvingId(item.id)} data-testid={`hr-resolve-btn-${item.id}`}>Mark Resolved</button>
                  )
                )}
              </div>
            ))}

            <div>
              <h4 className="font-semibold text-xs uppercase text-slate-500 mb-1">IT Notes</h4>
              {(c.it_notes || []).length === 0 ? <p className="text-xs text-slate-400">No notes recorded.</p> :
                c.it_notes.map(n => (
                  <div key={n.id} className="border-l-2 border-slate-200 pl-2 mb-1 text-xs" data-testid="hr-note-item">
                    <div>{n.note}</div>
                    <div className="text-slate-400">{n.created_by_name} · {fmtDT(n.created_at)}</div>
                  </div>
                ))}
            </div>

            <div>
              <h4 className="font-semibold text-xs uppercase text-slate-500 mb-1">Supporting Documents</h4>
              {(c.attachments || []).length === 0 ? <p className="text-xs text-slate-400">No documents attached.</p> :
                c.attachments.map(a => (
                  <div key={a.file_id} className="flex items-center gap-2 text-xs py-0.5" data-testid="hr-attachment-item">
                    <span className="flex-1">{a.filename} <span className="text-slate-400">({Math.round(a.file_size / 1024)} KB · {a.uploaded_by_name})</span></span>
                    <button className="text-blue-600 hover:underline flex items-center gap-1" onClick={() => download(a.file_id, a.filename)} data-testid={`hr-attachment-download-${a.file_id}`}>
                      <Download size={12} /> Download
                    </button>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
